// Mirror of WebhookEvent in packages/db/src/schema/webhooks.ts. Kept in
// shared so the FE form can validate without importing from db.
export const WEBHOOK_EVENT_CODES = [
  'post.published',
  'post.unpublished',
  'post.deleted',
  'post.updated',
] as const;

export type WebhookEventCode = (typeof WEBHOOK_EVENT_CODES)[number];

export interface WebhookListItem {
  id: string;
  url: string;
  events: WebhookEventCode[];
  isActive: boolean;
  description: string | null;
  /** ISO timestamp of the most recent delivery attempt. Null when the
   *  hook has never fired (newly created, no event has triggered yet). */
  lastDeliveryAt: string | null;
  /** Outcome of that most-recent attempt. Null when lastDeliveryAt is
   *  null. Drives the inline ✓/✗ indicator on the /webhooks row. */
  lastDeliveryOk: boolean | null;
  createdAt: string;
  updatedAt: string;
}

export interface WebhookDetail extends WebhookListItem {
  /** Only returned on the create response (so the user can save it once). */
  secret?: string;
}

export interface WebhookTestResult {
  ok: boolean;
  status: number;
  durationMs: number;
  error?: string;
}

/** Body sent to the receiver on every delivery. */
export interface WebhookPayload<T = unknown> {
  event: WebhookEventCode;
  occurredAt: string; // ISO timestamp
  deliveryId: string; // unique per delivery — for receiver dedup
  data: T;
}

/** Aggregate stats across all webhook subscriptions for the dashboard widget. */
export interface WebhookStats {
  /** Active subscriptions (is_active=true). */
  activeWebhooks: number;
  /** Total subscriptions including inactive. */
  totalWebhooks: number;
  /** Delivery attempts in the rolling window. */
  windowDays: number;
  totalAttempts: number;
  successCount: number;
  failCount: number;
  /** 0-100; -1 when no attempts in window. */
  successRate: number;
}

/** Row from the webhook_deliveries audit table. */
export interface WebhookDeliveryRecord {
  id: string;
  webhookId: string;
  event: WebhookEventCode;
  deliveryId: string;
  ok: boolean;
  status: number;
  durationMs: number;
  error: string | null;
  deliveredAt: string; // ISO
}
